import { useCallback, useEffect, useState, type FormEvent } from "react";
import { portalApi } from "../api";
import { errorText } from "../errors";
import { useI18n } from "../i18n";
import type { Viewer } from "../types";
import { Field, Notice } from "../ui";

/**
 * "Tittarkonton" on the purchaser's plan page (ORG-6, Sigge 2026-10-06:
 * "think of it as when you share a document in google drive"). One email
 * field shares the organisation's films with a colleague, who gets a link to
 * choose a password; each shared person can be revoked, which signs them out
 * at once. Sharing again lets a revoked colleague back in. Unlimited.
 */

const SHARE_ERRORS = {
  email_taken: "viewers.err.emailTaken",
  already_shared: "viewers.err.alreadyShared",
  invalid_request: "viewers.err.invalid",
  no_plan: "plan.err.noPlan",
} as const;

export function ViewersSection() {
  const { t } = useI18n();
  const [viewers, setViewers] = useState<Viewer[] | null>(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setViewers((await portalApi.orgViewers()).viewers);
    } catch (err) {
      setError(errorText(err, t, SHARE_ERRORS));
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function share(e: FormEvent) {
    e.preventDefault();
    const to = email.trim();
    if (!to) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await portalApi.orgShare(to);
      setNotice(
        r.viewer.status === "active"
          ? t("viewers.restored", { email: to })
          : t("viewers.shared", { email: to })
      );
      setEmail("");
      await load();
    } catch (err) {
      setError(errorText(err, t, SHARE_ERRORS));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(v: Viewer) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await portalApi.orgRevoke(v.id);
      setNotice(t("viewers.revoked", { email: v.email }));
      await load();
    } catch (err) {
      setError(errorText(err, t, SHARE_ERRORS));
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  }

  const live = (viewers ?? []).filter((v) => v.status !== "revoked");
  const revoked = (viewers ?? []).filter((v) => v.status === "revoked");

  return (
    <section className="panel" aria-labelledby="plan-viewers">
      <h2 id="plan-viewers">{t("viewers.title")}</h2>
      <p className="field__hint">{t("viewers.hint")}</p>
      {notice && <Notice kind="success">{notice}</Notice>}
      {error && <Notice kind="error">{error}</Notice>}
      <form className="viewers__share" onSubmit={(e) => void share(e)}>
        <Field
          label={t("viewers.email")}
          type="email"
          autoComplete="off"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <button
          type="submit"
          className="btn btn--primary"
          disabled={busy || !email.trim()}
        >
          {t("viewers.share")}
        </button>
      </form>
      {viewers !== null && live.length === 0 && <p>{t("viewers.none")}</p>}
      {live.length > 0 && (
        <ul className="viewers__list">
          {live.map((v) => (
            <li key={v.id} className="viewers__row">
              <span className="viewers__email">{v.email}</span>
              <span className="viewers__status">
                {v.status === "active"
                  ? t("viewers.statusActive")
                  : t("viewers.statusInvited")}
              </span>
              {confirming === v.id ? (
                <span className="viewers__confirm">
                  <button
                    type="button"
                    className="btn btn--danger"
                    disabled={busy}
                    onClick={() => void revoke(v)}
                  >
                    {t("viewers.revokeConfirm")}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setConfirming(null)}
                  >
                    {t("common.cancel")}
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={busy}
                  onClick={() => setConfirming(v.id)}
                  aria-label={t("viewers.revokeFor", { email: v.email })}
                >
                  {t("viewers.revoke")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {revoked.length > 0 && (
        <details className="viewers__revoked">
          <summary>{t("viewers.revokedList", { n: revoked.length })}</summary>
          <ul>
            {revoked.map((v) => (
              <li key={v.id}>{v.email}</li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
